import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, BookOpen, Phone, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteData } from '../contexts/SiteDataContext';

const Header = () => {
  const { author } = useSiteData();
  const authorData = author || {};
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const menuItems = [
    { label: 'Home', labelHindi: 'होम', href: '#home' },
    { label: 'About', labelHindi: 'परिचय', href: '#about' },
    { label: 'Book', labelHindi: 'पुस्तक', href: '#book-section' },
    { label: 'Reviews', labelHindi: 'समीक्षा', href: '#reviews' },
    { label: 'Gallery', labelHindi: 'गैलरी', href: '#gallery' },
    { label: 'Blog', labelHindi: 'ब्लॉग', href: '#blog' },
    { label: 'Contact', labelHindi: 'संपर्क', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => scrollToSection('#home')}
            whileHover={{ scale: 1.05 }}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-blue-900" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{authorData.name}</h1>
              <p className="text-xs text-gray-600" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>लेखक • राष्ट्रचिंतक</p>
            </div>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {menuItems.map((item, index) => (
              <motion.button
                key={index}
                onClick={() => scrollToSection(item.href)}
                className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-300 relative group"
                style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                whileHover={{ scale: 1.05 }}
              >
                {item.labelHindi}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-orange-600 group-hover:w-full transition-all duration-300" />
              </motion.button>
            ))}
            <Button 
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
              onClick={() => window.open(`https://wa.me/${authorData.whatsapp}`, '_blank')}
            >
              <Phone className="mr-2 h-4 w-4" />
              संपर्क
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-orange-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 py-4 space-y-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg px-6"
            >
              {menuItems.map((item, index) => (
                <motion.button
                  key={index}
                  onClick={() => scrollToSection(item.href)}
                  className="block w-full text-left text-gray-700 hover:text-orange-600 font-medium transition-colors py-2"
                  style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {item.labelHindi}
                </motion.button>
              ))}
              <Button 
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                onClick={() => window.open(`https://wa.me/${authorData.whatsapp}`, '_blank')}
              >
                <Phone className="mr-2 h-4 w-4" />
                संपर्क
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;